import * as THREE from "three";
import BaseThreeScene from "../../scenes/BaseThreeScene";
import { createSeatBlock } from "../../scenes/arrayPrimitives";
import {
    createTextSprite,
    updateTextSprite,
} from "../../scenes/shared/textSpritePrimitives";

const DEFAULT_NUMS = [100, 4, 200, 1, 3, 2];

const INPUT_IDLE_COLOR = new THREE.Color(0x334155);
const SET_IDLE_COLOR = new THREE.Color(0x1e293b);
const ACTIVE_COLOR = new THREE.Color(0xfbbf24);
const PREDECESSOR_COLOR = new THREE.Color(0xef4444);
const CURRENT_STREAK_COLOR = new THREE.Color(0x22c55e);
const BEST_STREAK_COLOR = new THREE.Color(0xa855f7);
const COMPLETE_COLOR = new THREE.Color(0x14532d);

function normalizeNumsInput(input) {
    return Array.isArray(input) ? input.slice() : DEFAULT_NUMS.slice();
}

function getUniqueSortedValues(nums) {
    return Array.from(new Set(nums)).sort((a, b) => a - b);
}

function formatValueLabel(value) {
    return value === null || value === undefined ? "-" : `${value}`;
}

function formatSequenceLabel(sequenceValues) {
    if (!sequenceValues || sequenceValues.length === 0) {
        return "-";
    }

    return `[${sequenceValues.join(", ")}]`;
}

function isBetterSequence(candidateSequence, bestSequence) {
    if (!bestSequence || bestSequence.length === 0) {
        return true;
    }

    if (candidateSequence.length > bestSequence.length) {
        return true;
    }

    if (candidateSequence.length < bestSequence.length) {
        return false;
    }

    return candidateSequence[0] < bestSequence[0];
}

function buildLongestConsecutiveSteps(nums) {
    const uniqueSortedValues = getUniqueSortedValues(nums);
    const valueSet = new Set(uniqueSortedValues);
    const steps = [
        {
            type: "start",
            nums: nums.slice(),
            uniqueSortedValues: uniqueSortedValues.slice(),
        },
        {
            type: "build-set",
            nums: nums.slice(),
            uniqueSortedValues: uniqueSortedValues.slice(),
            processedCount: 0,
            bestSequenceValues: [],
        },
    ];

    let bestSequenceValues = [];

    uniqueSortedValues.forEach((value, index) => {
        const predecessorValue = value - 1;
        const hasPredecessor = valueSet.has(predecessorValue);

        steps.push({
            type: "inspect-value",
            value,
            predecessorValue: hasPredecessor ? predecessorValue : null,
            hasPredecessor,
            processedCount: index + 1,
            uniqueSortedValues: uniqueSortedValues.slice(),
            bestSequenceValues: bestSequenceValues.slice(),
            currentSequenceValues: hasPredecessor ? [] : [value],
        });

        if (hasPredecessor) {
            steps.push({
                type: "skip-value",
                value,
                predecessorValue,
                processedCount: index + 1,
                uniqueSortedValues: uniqueSortedValues.slice(),
                bestSequenceValues: bestSequenceValues.slice(),
                currentSequenceValues: [],
            });
            return;
        }

        const currentSequenceValues = [value];
        let nextValue = value + 1;

        steps.push({
            type: "start-streak",
            value,
            processedCount: index + 1,
            uniqueSortedValues: uniqueSortedValues.slice(),
            bestSequenceValues: bestSequenceValues.slice(),
            currentSequenceValues: currentSequenceValues.slice(),
        });

        while (valueSet.has(nextValue)) {
            currentSequenceValues.push(nextValue);

            steps.push({
                type: "extend-streak",
                value,
                extensionValue: nextValue,
                processedCount: index + 1,
                uniqueSortedValues: uniqueSortedValues.slice(),
                bestSequenceValues: bestSequenceValues.slice(),
                currentSequenceValues: currentSequenceValues.slice(),
            });

            nextValue += 1;
        }

        const didUpdateBest = isBetterSequence(
            currentSequenceValues,
            bestSequenceValues
        );

        if (didUpdateBest) {
            bestSequenceValues = currentSequenceValues.slice();
        }

        steps.push({
            type: "finish-streak",
            value,
            processedCount: index + 1,
            uniqueSortedValues: uniqueSortedValues.slice(),
            bestSequenceValues: bestSequenceValues.slice(),
            currentSequenceValues: currentSequenceValues.slice(),
            didUpdateBest,
        });
    });

    steps.push({
        type: "complete",
        nums: nums.slice(),
        uniqueSortedValues: uniqueSortedValues.slice(),
        processedCount: uniqueSortedValues.length,
        bestSequenceValues: bestSequenceValues.slice(),
        currentSequenceValues: [],
    });

    return {
        steps,
        uniqueSortedValues,
        bestSequenceValues,
    };
}

function createStepLogEntry(step, stepIndex) {
    const stepNumber = stepIndex + 1;

    if (step.type === "start") {
        return {
            id: `longest-consecutive-step-${stepNumber}-start`,
            stepIndex: stepNumber,
            kind: "Setup",
            title: "Initialize input",
            message: "The viewer starts with the original array and a deduplicated set view. The scan checks whether each value can begin a consecutive run.",
        };
    }

    if (step.type === "build-set") {
        return {
            id: `longest-consecutive-step-${stepNumber}-build-set`,
            stepIndex: stepNumber,
            kind: "Setup",
            title: "Build hash set",
            message: "The algorithm stores all unique values in a hash set so predecessor and successor lookups are constant time on average.",
        };
    }

    if (step.type === "inspect-value") {
        return {
            id: `longest-consecutive-step-${stepNumber}-inspect-${step.value}`,
            stepIndex: stepNumber,
            kind: "Inspect",
            title: `Inspect ${step.value}`,
            message: step.hasPredecessor
                ? `${step.value} has predecessor ${step.predecessorValue}, so it cannot be the start of a new longest run.`
                : `${step.value} has no predecessor in the set, so it is the start of a new consecutive run.`,
        };
    }

    if (step.type === "skip-value") {
        return {
            id: `longest-consecutive-step-${stepNumber}-skip-${step.value}`,
            stepIndex: stepNumber,
            kind: "Skip",
            title: `Skip ${step.value}`,
            message: `Because ${step.predecessorValue} exists, starting from ${step.value} would duplicate work already covered by an earlier run.`,
        };
    }

    if (step.type === "start-streak") {
        return {
            id: `longest-consecutive-step-${stepNumber}-start-streak-${step.value}`,
            stepIndex: stepNumber,
            kind: "Start",
            title: `Start streak at ${step.value}`,
            message: `Initialize the current streak with ${step.value} and then keep extending while the next integer exists in the set.`,
        };
    }

    if (step.type === "extend-streak") {
        return {
            id: `longest-consecutive-step-${stepNumber}-extend-${step.extensionValue}`,
            stepIndex: stepNumber,
            kind: "Extend",
            title: `Extend streak to ${step.extensionValue}`,
            message: `${step.extensionValue} exists in the set, so it extends the active consecutive sequence to ${formatSequenceLabel(step.currentSequenceValues)}.`,
        };
    }

    if (step.type === "finish-streak") {
        return {
            id: `longest-consecutive-step-${stepNumber}-finish-${step.value}`,
            stepIndex: stepNumber,
            kind: "Commit",
            title: `Finish streak from ${step.value}`,
            message: step.didUpdateBest
                ? `The streak ${formatSequenceLabel(step.currentSequenceValues)} becomes the new best sequence.`
                : `The streak ${formatSequenceLabel(step.currentSequenceValues)} is valid, but it does not beat the current best sequence ${formatSequenceLabel(step.bestSequenceValues)}.`,
        };
    }

    return {
        id: `longest-consecutive-step-${stepNumber}-complete`,
        stepIndex: stepNumber,
        kind: "Result",
        title: "Longest streak complete",
        message: `The longest consecutive sequence is ${formatSequenceLabel(step.bestSequenceValues)}, so the answer is ${step.bestSequenceValues.length}.`,
    };
}

export default class LongestConsecutiveSequenceScene extends BaseThreeScene {
    setup(props) {
        this.nums = normalizeNumsInput(props.input);
        const plan = buildLongestConsecutiveSteps(this.nums);

        this.steps = plan.steps;
        this.uniqueSortedValues = plan.uniqueSortedValues;
        this.finalBestSequenceValues = plan.bestSequenceValues;
        this.inputIndexByValue = new Map();

        this.nums.forEach((value, index) => {
            if (!this.inputIndexByValue.has(value)) {
                this.inputIndexByValue.set(value, []);
            }

            this.inputIndexByValue.get(value).push(index);
        });

        this.scene.background = new THREE.Color(0x030712);
        this.scene.fog = new THREE.Fog(0x030712, 10, 36);

        this.inputSpacing = this.nums.length > 10 ? 1.0 : 1.18;
        this.setSpacing = this.uniqueSortedValues.length > 10 ? 1.04 : 1.24;
        this.inputSpan =
            Math.max(0, this.nums.length - 1) * this.inputSpacing + 1.0;
        this.setSpan =
            Math.max(0, this.uniqueSortedValues.length - 1) * this.setSpacing +
            1.02;
        this.boardWidth = Math.max(this.inputSpan, this.setSpan) + 2.8;

        this.camera.position.set(0, 5.6, 10.8 + this.boardWidth * 0.12);
        this.camera.lookAt(0, 0.55, 0);

        const ambientLight = new THREE.AmbientLight(0xffffff, 1.06);
        this.scene.add(ambientLight);

        const keyLight = new THREE.DirectionalLight(0xffffff, 1.65);
        keyLight.position.set(5, 8, 5);
        this.scene.add(keyLight);

        const fillLight = new THREE.PointLight(0x60a5fa, 18, 20, 2);
        fillLight.position.set(-4, 4, 4);
        this.scene.add(fillLight);

        const rimLight = new THREE.PointLight(0xa855f7, 16, 18, 2);
        rimLight.position.set(4, 5, -2);
        this.scene.add(rimLight);

        this.floor = new THREE.Mesh(
            new THREE.PlaneGeometry(this.boardWidth, 8.6),
            new THREE.MeshBasicMaterial({
                color: 0x071226,
                transparent: true,
                opacity: 0.94,
            })
        );
        this.floor.rotation.x = -Math.PI * 0.5;
        this.floor.position.set(0, -1.7, 0.8);
        this.scene.add(this.floor);

        this.rowTitles = [];
        this.inputTileRoot = new THREE.Group();
        this.setTileRoot = new THREE.Group();
        this.labelRoot = new THREE.Group();
        this.scene.add(this.inputTileRoot);
        this.scene.add(this.setTileRoot);
        this.scene.add(this.labelRoot);

        this.inputPositions = new Map();
        this.setPositions = new Map();
        this.inputMeshes = new Map();
        this.inputValueLabels = new Map();
        this.inputIndexLabels = new Map();
        this.setMeshes = new Map();
        this.setValueLabels = new Map();
        this.setIndexLabels = new Map();

        const inputY = 1.45;
        const setY = -0.45;
        const inputOriginX = -((this.nums.length - 1) * this.inputSpacing) * 0.5;
        const setOriginX =
            -((this.uniqueSortedValues.length - 1) * this.setSpacing) * 0.5;

        const inputTitle = createTextSprite({
            text: "input array",
            width: 188,
            height: 64,
            fontSize: 22,
            textColor: "#e2e8f0",
            fillStyle: "rgba(15, 23, 42, 0.9)",
            strokeStyle: "rgba(255, 255, 255, 0.08)",
            outlineColor: "rgba(0, 0, 0, 0.95)",
            outlineWidth: 6,
            scale: [1.02, 0.34],
        });
        inputTitle.position.set(0, 2.45, 0);
        this.rowTitles.push(inputTitle);
        this.labelRoot.add(inputTitle);

        const setTitle = createTextSprite({
            text: "hash set view (sorted)",
            width: 250,
            height: 64,
            fontSize: 22,
            textColor: "#e2e8f0",
            fillStyle: "rgba(15, 23, 42, 0.9)",
            strokeStyle: "rgba(255, 255, 255, 0.08)",
            outlineColor: "rgba(0, 0, 0, 0.95)",
            outlineWidth: 6,
            scale: [1.34, 0.34],
        });
        setTitle.position.set(0, 0.48, 0);
        this.rowTitles.push(setTitle);
        this.labelRoot.add(setTitle);

        this.nums.forEach((value, index) => {
            const x = inputOriginX + index * this.inputSpacing;
            this.inputPositions.set(index, new THREE.Vector3(x, inputY, 0));

            const tile = createSeatBlock({
                occupied: false,
                width: 0.94,
                height: 0.26,
                depth: 0.94,
                emptyColor: INPUT_IDLE_COLOR.getHex(),
                occupiedColor: INPUT_IDLE_COLOR.getHex(),
            });
            tile.position.set(x, inputY, 0);
            tile.userData.baseY = inputY;
            this.inputMeshes.set(index, tile);
            this.inputTileRoot.add(tile);

            const valueLabel = createTextSprite({
                text: String(value),
                width: 122,
                height: 68,
                fontSize: 28,
                textColor: "#f8fafc",
                fillStyle: "rgba(15, 23, 42, 0.92)",
                strokeStyle: "rgba(255, 255, 255, 0.08)",
                outlineColor: "rgba(0, 0, 0, 0.95)",
                outlineWidth: 6,
                scale: [0.82, 0.42],
            });
            valueLabel.position.set(x, inputY + 0.62, 0);
            this.inputValueLabels.set(index, valueLabel);
            this.labelRoot.add(valueLabel);

            const indexLabel = createTextSprite({
                text: `${index}`,
                width: 84,
                height: 42,
                fontSize: 18,
                textColor: "#bfdbfe",
                fillStyle: "rgba(2, 6, 23, 0.76)",
                strokeStyle: "rgba(255, 255, 255, 0.04)",
                outlineColor: "rgba(0, 0, 0, 0.95)",
                outlineWidth: 5,
                scale: [0.44, 0.18],
            });
            indexLabel.position.set(x, inputY - 0.58, 0);
            this.inputIndexLabels.set(index, indexLabel);
            this.labelRoot.add(indexLabel);
        });

        this.uniqueSortedValues.forEach((value, index) => {
            const x = setOriginX + index * this.setSpacing;
            this.setPositions.set(value, new THREE.Vector3(x, setY, 0));

            const tile = createSeatBlock({
                occupied: false,
                width: 1.02,
                height: 0.3,
                depth: 1.02,
                emptyColor: SET_IDLE_COLOR.getHex(),
                occupiedColor: SET_IDLE_COLOR.getHex(),
            });
            tile.position.set(x, setY, 0);
            tile.userData.baseY = setY;
            this.setMeshes.set(value, tile);
            this.setTileRoot.add(tile);

            const valueLabel = createTextSprite({
                text: String(value),
                width: 128,
                height: 72,
                fontSize: 30,
                textColor: "#f8fafc",
                fillStyle: "rgba(15, 23, 42, 0.92)",
                strokeStyle: "rgba(255, 255, 255, 0.08)",
                outlineColor: "rgba(0, 0, 0, 0.95)",
                outlineWidth: 6,
                scale: [0.9, 0.44],
            });
            valueLabel.position.set(x, setY + 0.7, 0);
            this.setValueLabels.set(value, valueLabel);
            this.labelRoot.add(valueLabel);

            const indexLabel = createTextSprite({
                text: `${index}`,
                width: 84,
                height: 42,
                fontSize: 18,
                textColor: "#bfdbfe",
                fillStyle: "rgba(2, 6, 23, 0.76)",
                strokeStyle: "rgba(255, 255, 255, 0.04)",
                outlineColor: "rgba(0, 0, 0, 0.95)",
                outlineWidth: 5,
                scale: [0.44, 0.18],
            });
            indexLabel.position.set(x, setY - 0.62, 0);
            this.setIndexLabels.set(value, indexLabel);
            this.labelRoot.add(indexLabel);
        });

        this.currentStreakBand = new THREE.Mesh(
            new THREE.PlaneGeometry(1, 0.82),
            new THREE.MeshBasicMaterial({
                color: CURRENT_STREAK_COLOR.getHex(),
                transparent: true,
                opacity: 0,
                depthWrite: false,
            })
        );
        this.currentStreakBand.position.set(0, setY + 0.02, -0.54);
        this.scene.add(this.currentStreakBand);

        this.bestStreakBand = new THREE.Mesh(
            new THREE.PlaneGeometry(1, 0.34),
            new THREE.MeshBasicMaterial({
                color: BEST_STREAK_COLOR.getHex(),
                transparent: true,
                opacity: 0,
                depthWrite: false,
            })
        );
        this.bestStreakBand.position.set(0, setY - 0.62, -0.58);
        this.scene.add(this.bestStreakBand);

        this.activeInputRing = new THREE.Mesh(
            new THREE.TorusGeometry(0.74, 0.04, 20, 52),
            new THREE.MeshBasicMaterial({
                color: ACTIVE_COLOR.getHex(),
                transparent: true,
                opacity: 0,
            })
        );
        this.activeInputRing.rotation.x = Math.PI * 0.5;
        this.activeInputRing.position.set(0, inputY - 0.02, 0);
        this.scene.add(this.activeInputRing);

        this.activeSetRing = new THREE.Mesh(
            new THREE.TorusGeometry(0.8, 0.045, 20, 56),
            new THREE.MeshBasicMaterial({
                color: ACTIVE_COLOR.getHex(),
                transparent: true,
                opacity: 0,
            })
        );
        this.activeSetRing.rotation.x = Math.PI * 0.5;
        this.activeSetRing.position.set(0, setY - 0.02, 0);
        this.scene.add(this.activeSetRing);

        this.predecessorRing = new THREE.Mesh(
            new THREE.TorusGeometry(0.84, 0.045, 20, 56),
            new THREE.MeshBasicMaterial({
                color: PREDECESSOR_COLOR.getHex(),
                transparent: true,
                opacity: 0,
            })
        );
        this.predecessorRing.rotation.x = Math.PI * 0.5;
        this.predecessorRing.position.set(0, setY - 0.02, 0);
        this.scene.add(this.predecessorRing);

        this.resetAnimationState();
        this.advanceStep();
    }

    resetAnimationState() {
        this.stepIndex = -1;
        this.currentStep = null;
        this.currentStepElapsed = 0;
        this.presentationTime = 0;

        this.processedCount = 0;
        this.activeValue = null;
        this.predecessorValue = null;
        this.currentSequenceValues = [];
        this.bestSequenceValues = [];
        this.statusLabel = "Build hash set";

        this.resetStepLog([]);
        this.replaceUiState({
            inputCount: this.nums.length,
            uniqueCount: this.uniqueSortedValues.length,
            processedCount: 0,
            activeValueLabel: "-",
            predecessorLabel: "-",
            currentSequenceLabel: "-",
            currentLengthLabel: "0",
            bestSequenceLabel: "-",
            bestLengthLabel: "0",
            statusLabel: this.statusLabel,
        });
        this.syncPlaybackState();
    }

    syncPlaybackState() {
        this.setPlaybackState({
            stepIndex: Math.max(0, this.stepIndex + 1),
            stepCount: this.steps.length,
            hasNextStep: this.stepIndex < this.steps.length - 1,
            isComplete:
                this.currentStep !== null && this.currentStep.type === "complete",
        });
    }

    syncUiState() {
        this.replaceUiState({
            inputCount: this.nums.length,
            uniqueCount: this.uniqueSortedValues.length,
            processedCount: this.processedCount,
            activeValueLabel: formatValueLabel(this.activeValue),
            predecessorLabel: formatValueLabel(this.predecessorValue),
            currentSequenceLabel: formatSequenceLabel(this.currentSequenceValues),
            currentLengthLabel: `${this.currentSequenceValues.length}`,
            bestSequenceLabel: formatSequenceLabel(this.bestSequenceValues),
            bestLengthLabel: `${this.bestSequenceValues.length}`,
            statusLabel: this.statusLabel,
        });
    }

    getStepDuration(step) {
        if (step.type === "start" || step.type === "build-set") {
            return 1.0;
        }

        if (step.type === "inspect-value" || step.type === "skip-value") {
            return 0.95;
        }

        if (step.type === "start-streak") {
            return 0.9;
        }

        if (step.type === "extend-streak") {
            return 0.85;
        }

        if (step.type === "finish-streak") {
            return 1.0;
        }

        return 2.0;
    }

    advanceStep() {
        const nextStepIndex = this.stepIndex + 1;

        if (nextStepIndex >= this.steps.length) {
            this.syncPlaybackState();
            this.syncUiState();
            return false;
        }

        this.stepIndex = nextStepIndex;
        this.currentStepElapsed = 0;
        this.currentStep = this.steps[this.stepIndex] || null;
        this.applyStep(this.currentStep);
        this.appendStepLog(createStepLogEntry(this.currentStep, this.stepIndex));
        this.syncPlaybackState();
        this.syncUiState();

        return true;
    }

    applyStep(step) {
        if (!step) {
            return;
        }

        this.processedCount = step.processedCount || 0;

        if (step.type === "start") {
            this.activeValue = null;
            this.predecessorValue = null;
            this.currentSequenceValues = [];
            this.bestSequenceValues = [];
            this.statusLabel = "Initialize input";
            return;
        }

        if (step.type === "build-set") {
            this.activeValue = null;
            this.predecessorValue = null;
            this.currentSequenceValues = [];
            this.bestSequenceValues = [];
            this.statusLabel = "Build deduplicated hash set";
            return;
        }

        if (step.type === "inspect-value") {
            this.activeValue = step.value;
            this.predecessorValue = step.predecessorValue;
            this.currentSequenceValues = step.currentSequenceValues.slice();
            this.bestSequenceValues = step.bestSequenceValues.slice();
            this.statusLabel = step.hasPredecessor
                ? `${step.value} has predecessor ${step.predecessorValue}`
                : `${step.value} can begin a streak`;
            return;
        }

        if (step.type === "skip-value") {
            this.activeValue = step.value;
            this.predecessorValue = step.predecessorValue;
            this.currentSequenceValues = [];
            this.bestSequenceValues = step.bestSequenceValues.slice();
            this.statusLabel = `Skip ${step.value} because ${step.predecessorValue} exists`;
            return;
        }

        if (step.type === "start-streak") {
            this.activeValue = step.value;
            this.predecessorValue = null;
            this.currentSequenceValues = step.currentSequenceValues.slice();
            this.bestSequenceValues = step.bestSequenceValues.slice();
            this.statusLabel = `Start streak at ${step.value}`;
            return;
        }

        if (step.type === "extend-streak") {
            this.activeValue = step.extensionValue;
            this.predecessorValue = null;
            this.currentSequenceValues = step.currentSequenceValues.slice();
            this.bestSequenceValues = step.bestSequenceValues.slice();
            this.statusLabel = `Extend streak to ${step.extensionValue}`;
            return;
        }

        if (step.type === "finish-streak") {
            this.activeValue = step.value;
            this.predecessorValue = null;
            this.currentSequenceValues = step.currentSequenceValues.slice();
            this.bestSequenceValues = step.bestSequenceValues.slice();
            this.statusLabel = step.didUpdateBest
                ? `Update best streak to length ${step.bestSequenceValues.length}`
                : "Keep previous best streak";
            return;
        }

        this.activeValue = null;
        this.predecessorValue = null;
        this.currentSequenceValues = [];
        this.bestSequenceValues = step.bestSequenceValues.slice();
        this.statusLabel = "Longest streak complete";
    }

    onResetPlayback() {
        this.resetAnimationState();
        this.advanceStep();
    }

    onStepForward() {
        this.advanceStep();
    }

    update(delta) {
        if (!this.currentStep) {
            return;
        }

        const playbackDelta = this.isPlaying ? delta : 0;
        this.presentationTime += playbackDelta;
        this.currentStepElapsed += playbackDelta;

        if (
            this.isPlaying &&
            this.currentStepElapsed >= this.getStepDuration(this.currentStep)
        ) {
            const advanced = this.advanceStep();

            if (!advanced) {
                this.pause();
            }
        }

        this.updateInputRow(this.presentationTime);
        this.updateSetRow(this.presentationTime);
        this.updateCurrentStreakBand();
        this.updateBestStreakBand();
        this.updateMarkers(this.presentationTime);
    }

    updateInputRow(elapsed) {
        this.nums.forEach((value, index) => {
            const tile = this.inputMeshes.get(index);
            const valueLabel = this.inputValueLabels.get(index);
            const isActive = this.activeValue !== null && value === this.activeValue;

            let targetColor = INPUT_IDLE_COLOR.clone();
            let targetScale = 1;
            let targetEmissiveIntensity = 0.16;

            if (isActive) {
                targetColor = ACTIVE_COLOR.clone();
                targetScale =
                    1.12 + Math.sin(elapsed * 5.4 + index * 0.2) * 0.02;
                targetEmissiveIntensity = 0.78;
            }

            tile.material.color.lerp(targetColor, 0.16);
            tile.material.emissive.lerp(targetColor, 0.16);
            tile.material.emissiveIntensity = THREE.MathUtils.lerp(
                tile.material.emissiveIntensity,
                targetEmissiveIntensity,
                0.16
            );

            const nextScale = THREE.MathUtils.lerp(tile.scale.x, targetScale, 0.16);
            tile.scale.set(
                nextScale,
                THREE.MathUtils.lerp(tile.scale.y, targetScale, 0.16),
                nextScale
            );
            tile.position.y = THREE.MathUtils.lerp(
                tile.position.y,
                tile.userData.baseY + Math.sin(elapsed * 1.8 + index * 0.35) * 0.02,
                0.12
            );

            updateTextSprite(valueLabel, String(value), {
                fontSize: 28,
                textColor: "#f8fafc",
                fillStyle: isActive
                    ? "rgba(146, 64, 14, 0.96)"
                    : "rgba(15, 23, 42, 0.92)",
                strokeStyle: "rgba(255, 255, 255, 0.08)",
                outlineColor: "rgba(0, 0, 0, 0.95)",
                outlineWidth: 6,
            });
            valueLabel.position.y = THREE.MathUtils.lerp(
                valueLabel.position.y,
                tile.position.y + 0.62,
                0.18
            );
        });
    }

    updateSetRow(elapsed) {
        const currentSet = new Set(this.currentSequenceValues);
        const bestSet = new Set(this.bestSequenceValues);

        this.uniqueSortedValues.forEach((value) => {
            const tile = this.setMeshes.get(value);
            const valueLabel = this.setValueLabels.get(value);
            const activeTag =
                value === this.activeValue
                    ? "active"
                    : value === this.predecessorValue
                      ? "predecessor"
                      : currentSet.has(value)
                        ? "current"
                        : bestSet.has(value)
                          ? "best"
                          : null;

            let targetColor = SET_IDLE_COLOR.clone();
            let targetScale = 1;
            let targetEmissiveIntensity = 0.16;
            let labelFill = "rgba(15, 23, 42, 0.92)";

            if (activeTag === "best") {
                targetColor = BEST_STREAK_COLOR.clone();
                targetEmissiveIntensity = 0.32;
                labelFill = "rgba(107, 33, 168, 0.96)";
            }

            if (activeTag === "current") {
                targetColor = CURRENT_STREAK_COLOR.clone();
                targetEmissiveIntensity = 0.38;
                labelFill = "rgba(21, 128, 61, 0.96)";
            }

            if (activeTag === "predecessor") {
                targetColor = PREDECESSOR_COLOR.clone();
                targetEmissiveIntensity = 0.52;
                labelFill = "rgba(153, 27, 27, 0.96)";
            }

            if (activeTag === "active") {
                targetColor = ACTIVE_COLOR.clone();
                targetScale = 1.12 + Math.sin(elapsed * 5.2) * 0.02;
                targetEmissiveIntensity = 0.88;
                labelFill = "rgba(146, 64, 14, 0.96)";
            }

            if (this.currentStep && this.currentStep.type === "complete") {
                targetColor = bestSet.has(value)
                    ? COMPLETE_COLOR.clone().lerp(BEST_STREAK_COLOR, 0.25)
                    : SET_IDLE_COLOR.clone();
                targetEmissiveIntensity = bestSet.has(value) ? 0.4 : 0.16;
                labelFill = bestSet.has(value)
                    ? "rgba(20, 83, 45, 0.96)"
                    : "rgba(15, 23, 42, 0.92)";
            }

            tile.material.color.lerp(targetColor, 0.16);
            tile.material.emissive.lerp(targetColor, 0.16);
            tile.material.emissiveIntensity = THREE.MathUtils.lerp(
                tile.material.emissiveIntensity,
                targetEmissiveIntensity,
                0.16
            );

            const nextScale = THREE.MathUtils.lerp(tile.scale.x, targetScale, 0.16);
            tile.scale.set(
                nextScale,
                THREE.MathUtils.lerp(tile.scale.y, targetScale, 0.16),
                nextScale
            );
            tile.position.y = THREE.MathUtils.lerp(
                tile.position.y,
                tile.userData.baseY + Math.sin(elapsed * 1.65 + value * 0.13) * 0.02,
                0.12
            );

            updateTextSprite(valueLabel, String(value), {
                fontSize: 30,
                textColor: "#f8fafc",
                fillStyle: labelFill,
                strokeStyle: "rgba(255, 255, 255, 0.08)",
                outlineColor: "rgba(0, 0, 0, 0.95)",
                outlineWidth: 6,
            });
            valueLabel.position.y = THREE.MathUtils.lerp(
                valueLabel.position.y,
                tile.position.y + 0.7,
                0.18
            );
        });
    }

    updateBand(mesh, sequenceValues, opacity, y) {
        if (!sequenceValues || sequenceValues.length === 0) {
            mesh.material.opacity = THREE.MathUtils.lerp(
                mesh.material.opacity,
                0,
                0.16
            );
            return;
        }

        const startPosition = this.setPositions.get(sequenceValues[0]);
        const endPosition = this.setPositions.get(
            sequenceValues[sequenceValues.length - 1]
        );

        if (!startPosition || !endPosition) {
            return;
        }

        const centerX = (startPosition.x + endPosition.x) * 0.5;
        const width = Math.abs(endPosition.x - startPosition.x) + 1.1;

        mesh.position.x = THREE.MathUtils.lerp(mesh.position.x, centerX, 0.16);
        mesh.position.y = THREE.MathUtils.lerp(mesh.position.y, y, 0.16);
        mesh.scale.x = THREE.MathUtils.lerp(mesh.scale.x, width, 0.16);
        mesh.material.opacity = THREE.MathUtils.lerp(
            mesh.material.opacity,
            opacity,
            0.16
        );
    }

    updateCurrentStreakBand() {
        this.updateBand(
            this.currentStreakBand,
            this.currentSequenceValues,
            0.26,
            -0.42
        );
    }

    updateBestStreakBand() {
        this.updateBand(
            this.bestStreakBand,
            this.bestSequenceValues,
            0.3,
            -1.02
        );
    }

    updateMarkers(elapsed) {
        const activeInputIndices =
            this.activeValue === null
                ? []
                : this.inputIndexByValue.get(this.activeValue) || [];

        if (activeInputIndices.length === 0) {
            this.activeInputRing.material.opacity = THREE.MathUtils.lerp(
                this.activeInputRing.material.opacity,
                0,
                0.18
            );
        } else {
            const position = this.inputPositions.get(activeInputIndices[0]);
            this.activeInputRing.position.x = THREE.MathUtils.lerp(
                this.activeInputRing.position.x,
                position.x,
                0.18
            );
            this.activeInputRing.position.y = THREE.MathUtils.lerp(
                this.activeInputRing.position.y,
                1.18 + Math.sin(elapsed * 5.2) * 0.03,
                0.18
            );
            this.activeInputRing.scale.setScalar(1 + Math.sin(elapsed * 5.2) * 0.04);
            this.activeInputRing.material.opacity = THREE.MathUtils.lerp(
                this.activeInputRing.material.opacity,
                0.92,
                0.18
            );
        }

        if (this.activeValue === null || !this.setPositions.has(this.activeValue)) {
            this.activeSetRing.material.opacity = THREE.MathUtils.lerp(
                this.activeSetRing.material.opacity,
                0,
                0.18
            );
        } else {
            const position = this.setPositions.get(this.activeValue);
            this.activeSetRing.position.x = THREE.MathUtils.lerp(
                this.activeSetRing.position.x,
                position.x,
                0.18
            );
            this.activeSetRing.position.y = THREE.MathUtils.lerp(
                this.activeSetRing.position.y,
                -0.42 + Math.sin(elapsed * 5.2) * 0.03,
                0.18
            );
            this.activeSetRing.scale.setScalar(1 + Math.sin(elapsed * 5.2) * 0.04);
            this.activeSetRing.material.opacity = THREE.MathUtils.lerp(
                this.activeSetRing.material.opacity,
                0.94,
                0.18
            );
        }

        if (
            this.predecessorValue === null ||
            !this.setPositions.has(this.predecessorValue)
        ) {
            this.predecessorRing.material.opacity = THREE.MathUtils.lerp(
                this.predecessorRing.material.opacity,
                0,
                0.18
            );
        } else {
            const position = this.setPositions.get(this.predecessorValue);
            this.predecessorRing.position.x = THREE.MathUtils.lerp(
                this.predecessorRing.position.x,
                position.x,
                0.18
            );
            this.predecessorRing.position.y = THREE.MathUtils.lerp(
                this.predecessorRing.position.y,
                -0.42 + Math.sin(elapsed * 4.2) * 0.02,
                0.18
            );
            this.predecessorRing.scale.setScalar(
                1 + Math.sin(elapsed * 4.2) * 0.03
            );
            this.predecessorRing.material.opacity = THREE.MathUtils.lerp(
                this.predecessorRing.material.opacity,
                0.88,
                0.18
            );
        }
    }

    onDispose() {
        const disposeSpriteTexture = (sprite) => {
            if (!sprite || !sprite.material || !sprite.material.map) {
                return;
            }

            sprite.material.map.dispose();
        };

        this.rowTitles.forEach(disposeSpriteTexture);
        this.inputValueLabels.forEach(disposeSpriteTexture);
        this.inputIndexLabels.forEach(disposeSpriteTexture);
        this.setValueLabels.forEach(disposeSpriteTexture);
        this.setIndexLabels.forEach(disposeSpriteTexture);
    }
}